import React, { useState, useEffect } from "react";
import axios from "axios";
import { Box } from "@mui/material";
import { DataGridPremium, GridToolbar } from "@mui/x-data-grid-premium";

const SalesReport = ({ startDate, endDate, downloadExcel, token }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pageSize, setPageSize] = useState(10);
  const [downloadInProgress, setDownloadInProgress] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);

      try {
        const url = `${
          process.env.REACT_APP_API_URL
        }/reports/greenNODEsales?startDate=${startDate}&endDate=${endDate}${
          downloadExcel ? "&download=true" : ""
        }`;

        const response = await axios.get(url, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          responseType: downloadExcel ? "blob" : "json",
        });

        if (downloadExcel) {
          setDownloadInProgress(true);
          const blob = new Blob([response.data], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          });
          const link = document.createElement("a");
          link.href = window.URL.createObjectURL(blob);
          link.download = `SalesExtract_${startDate}_${endDate}.xlsx`;
          document.body.appendChild(link);
          link.click();
          link.remove();
          setLoading(false);
          setDownloadInProgress(false);
          return;
        }

        // Expecting response.data.data to be an array of records like:
        // { lead_id, customer_id, order_id, status, ... }
        const rows = (response?.data?.data ?? []).map((row, idx) => ({
          ...row,
          // Ensure the grid always has a stable id
          id: row.lead_id ?? row.order_id ?? idx + 1,
        }));

        setData(rows);
      } catch (err) {
        setError(err?.message ?? "Failed to load report");
      } finally {
        setLoading(false);
      }
    };

    if (startDate && endDate) {
      fetchData();
    }
  }, [startDate, endDate, downloadExcel, token]);

  const formatDateTime = (value) => {
    if (!value) return "";
    const d = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(d.getTime())) return String(value);
    return new Intl.DateTimeFormat("en-ZA", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    }).format(d);
  };

  const columns = [
    { field: "client", headerName: "Client", minWidth: 120, flex: 1 },
    {
      field: "campaign_id",
      headerName: "Campaign ID",
      minWidth: 120,
      flex: 1,
      type: "number",
    },
    {
      field: "campaign_name",
      headerName: "Campaign Name",
      minWidth: 260,
      flex: 1.8,
    },
    { field: "username", headerName: "Username", minWidth: 240, flex: 1.6 },
    {
      field: "order_id",
      headerName: "Order ID",
      minWidth: 110,
      flex: 1,
      type: "number",
    },

    { field: "status", headerName: "Status", minWidth: 120, flex: 1 },
    {
      field: "status_reason",
      headerName: "Status Reason",
      minWidth: 160,
      flex: 1.2,
    },

    {
      field: "check_date",
      headerName: "Check Date",
      minWidth: 190,
      flex: 1.2,
      valueFormatter: ({ value }) => formatDateTime(value),
    },

    {
      field: "sale_date",
      headerName: "Sale Date",
      minWidth: 190,
      flex: 1.2,
      valueFormatter: ({ value }) => formatDateTime(value),
    },

    { field: "id_number", headerName: "ID Number", minWidth: 150, flex: 1.1 },

    {
      field: "taskflow_premium",
      headerName: "Premium",
      minWidth: 160,
      flex: 1,
      type: "number",
    },

    { field: "ext_status", headerName: "Ext Status", minWidth: 140, flex: 1 },
    {
      field: "ext_status_date",
      headerName: "Ext Status Date",
      minWidth: 190,
      flex: 1.2,
      valueFormatter: ({ value }) => formatDateTime(value),
    },

    {
      field: "export_accepted_date",
      headerName: "Export Accepted Date",
      minWidth: 210,
      flex: 1.3,
      valueFormatter: ({ value }) => formatDateTime(value),
    },
    {
      field: "export_rejection_date",
      headerName: "Export Rejection Date",
      minWidth: 210,
      flex: 1.3,
      valueFormatter: ({ value }) => formatDateTime(value),
    },
    {
      field: "export_rejection_reason",
      headerName: "Export Rejection Reason",
      minWidth: 220,
      flex: 1.5,
    },

    {
      field: "verification_status",
      headerName: "Verification Status",
      minWidth: 170,
      flex: 1.2,
    },
    { field: "policy_no", headerName: "Policy No", minWidth: 140, flex: 1 },

    {
      field: "client_order_ref",
      headerName: "Client Order Ref",
      minWidth: 170,
      flex: 1.2,
    },
    {
      field: "employee_number",
      headerName: "Employee Number",
      minWidth: 170,
      flex: 1.2,
    },
    {
      field: "directly_reports_employee_number",
      headerName: "Reports To (Emp #)",
      minWidth: 200,
      flex: 1.3,
    },
    { field: "manager", headerName: "Manager", minWidth: 240, flex: 1.6 },
  ];

  if (loading) return <div>Loading...</div>;
  if (error) return <div>Error: {error}</div>;
  if (downloadExcel && downloadInProgress)
    return <div>Preparing download...</div>;
  if (downloadExcel && !downloadInProgress) return <div>Download ready!</div>;
  if (!data || data.length === 0) return <h3>No Data Available</h3>;

  return (
    <Box sx={{ height: 600, width: "100%", marginTop: 2 }}>
      <DataGridPremium
        rows={data}
        columns={columns}
        pageSize={pageSize}
        onPageSizeChange={(newSize) => setPageSize(newSize)}
        slots={{ toolbar: GridToolbar }}
        pageSizeOptions={[5, 10, 15, 20]}
        disableRowSelectionOnClick
        getRowId={(row) => row.id}
      />
    </Box>
  );
};

export default SalesReport;
